// components/portal/SourceAssetsDashboard.tsx
'use client';

import React, { useEffect, useMemo } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import { SegmentSwitch } from '@/components/ui/SegmentSwitch';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const SOURCE_VIEWS = [{ key: 'shortlists', label: 'Shortlist' }, { key: 'assets', label: 'Assets' }] as const;

export function SourceDashboardToggle() {
  const { kernel } = useKernel();
  const tab = useKernelStore(s => s.sourceDashboardTab);
  return <SegmentSwitch groupLabel="Source dashboard" options={SOURCE_VIEWS} value={tab} onChange={(value) => {
    const k = kernel.current;
    if (!k || k.getState().sourceDashboardTab === value) return;
    k.setState({ sourceDashboardTab: value, sourceOpeningRentRollId: null });
    k.logUserAction('Source dashboard switched', { tab: value });
  }} />;
}

export function SourceAssetsDashboard() {
  const { kernel } = useKernel();
  const assets = useKernelStore(s => s.sourceAssets);
  const properties = useKernelStore(s => s.inventory.Property.registry);
  const portfolios = useKernelStore(s => s.inventory.Portfolio.registry);
  useEffect(() => { void kernel.current?.loadSourceAssets(); }, []);
  const groups = useMemo(() => {
    const result = new Map<string, typeof properties[string][]>();
    for (const id of assets.ids) {
      const property = properties[id];
      if (!property) continue;
      const list = result.get(property.portfolioId) ?? [];
      list.push(property);
      result.set(property.portfolioId, list);
    }
    return [...result.entries()];
  }, [assets.ids, properties]);
  if (assets.status === 'idle' || assets.status === 'loading') return <p className="px-4 text-[#8E8E8E]" style={{ fontSize: 14 }} role="status">Loading assets…</p>;
  if (assets.status === 'error') return <div className="px-4 text-[#ECECEC]" style={{ fontSize: 14 }} role="alert"><p>{assets.error}</p><button onClick={() => void kernel.current?.loadSourceAssets()} className="mt-3 h-8 rounded-full border border-[#2A2A2A] px-3.5 hover:bg-white/[0.06]" style={{ fontSize: 14 }}>Retry</button></div>;
  // Sections spaced as the campaign groups on the Shortlist view: 32px apart, 16px semibold names.
  // pt-3: 32px under the title row, as the content of every other module home sits.
  return <div className="space-y-8 px-4 pb-8 pt-3" data-testid="source-assets-dashboard">
    {!groups.length && <p className="text-[#8E8E8E]" style={{ fontSize: 14 }}>No assets are available to your account.</p>}
    {groups.map(([portfolioId, rows]) => <section key={portfolioId}>
      <h2 className="mb-3 font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>{portfolios[portfolioId]?.name ?? rows[0]?.portfolioName ?? 'Portfolio'}</h2>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(28vmin,1fr))] gap-4">
        {rows.map(property => <button key={property.id} type="button" className="group flex min-h-[18vmin] flex-col items-center justify-center rounded-[16px] bg-[#1B1B1B] p-[3vmin] text-center transition-colors hover:bg-[#232323] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#444444]"
          onClick={() => {
            const k = kernel.current;
            if (!k) return;
            k.logUserAction('Source asset opened', { propertyId: property.id });
            void k.focusEntityUI('Property', property.id, 'RENT_ROLL').catch(error => {
              if (k.getState().sourceOpeningRentRollId === property.id) k.setState({ sourceOpeningRentRollId: null });
              k.handleError(error instanceof Error ? error.message : 'Could not open asset conversation.');
            });
          }}>
          {/* The asset's own logo, centred with its name (owner, 2026-09-26); initials when it has none. */}
          <span className="mb-3"><BrandMark logoUrl={property.logoUrl ?? null} name={property.name} size={56} radius={12} /></span>
          <span className="font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>{property.name}</span>
          <span className="mt-0.5 text-[#8E8E8E]" style={{ fontSize: 13 }}>{property.totalUnits} units · Master rent roll</span>
        </button>)}
      </div>
    </section>)}
  </div>;
}
